import React, {forwardRef, useCallback, useEffect, useRef} from 'react';

import {cn} from '../lib/utils';

type DialogOwnProps = {
  /** Whether the dialog is shown. The consumer owns this, not the element. */
  open: boolean;
  /**
   * A request to close. Escape arrives here as a request rather than as a
   * dismissal the browser has already carried out, so one code path closes the
   * dialog however the user asked for it.
   */
  onClose: () => void;
  /** Focused once the dialog opens, in place of the browser's own choice. */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  className?: string;
  children?: React.ReactNode;
};

/**
 * `onCancel` is owned by the primitive and deliberately not part of the public
 * props: the whole point of the element's `cancel` event here is that it is
 * translated into `onClose`, and a second consumer-supplied handler would
 * silently lose that translation.
 */
export type DialogProps = DialogOwnProps &
  Omit<React.DialogHTMLAttributes<HTMLDialogElement>, keyof DialogOwnProps | 'onCancel'>;

export type DialogSectionProps = React.HTMLAttributes<HTMLDivElement>;

// Colour, border and scrim resolve through Layer 2 roles; geometry, motion and
// the responsive boundary use Tailwind's own scales, because Layer 2 exposes no
// geometry, motion or scrim role and Layer 1 is never referenced by a component.
const dialogStyles = [
  // `display` is only overridden while the dialog is open. A bare `flex` is
  // user-origin CSS, which outranks the UA's `dialog:not([open]) {display:none}`
  // whatever the specificity, and would leave a closed dialog on screen.
  'open:flex flex-col overflow-hidden p-0',
  'border border-(--compass-border) bg-(--compass-surface) text-(--compass-on-surface)',
  // Constrained from 576px up: a gutter on every side, then the consumer's own
  // `max-w-*` and the content's height within it, with `max-h` keeping a long
  // dialog inside the viewport and its body — not the page — doing the
  // scrolling. The width is a gutter rather than `w-full` because a consumer
  // whose `max-w-*` exceeds the viewport would otherwise override the UA's own
  // max-width and fill the screen edge to edge, which is the one thing this
  // half of the breakpoint exists to prevent.
  'm-auto w-[calc(100%_-_2rem)] max-h-[85dvh] rounded-md',
  // Full screen below 576px. Tailwind's `max-sm` compiles against
  // `--breakpoint-sm: 576px`, so it is exactly `width < 576px`. The two `none`s
  // and the reset margin are what defeat the UA's own `calc(100% - 6px - 2em)`
  // caps and centring.
  'max-sm:m-0 max-sm:size-full max-sm:max-h-none max-sm:max-w-none',
  'max-sm:rounded-none max-sm:border-0',
  // Layer 2 exposes no scrim role, so this literal matches the rgba(0, 0, 0,
  // 0.5) MUI painted in both modes. Recorded in the local-deviation register.
  'backdrop:bg-[rgba(0,0,0,0.5)]',
  // A fade on Tailwind's own duration scale. `display` and `overlay` are
  // discrete properties, so they need `allow-discrete` for the element to stay
  // painted across the transition at all.
  'opacity-0 open:opacity-100 starting:open:opacity-0',
  'transition-[opacity,display,overlay] transition-discrete duration-150 ease-out',
  // AC14: the entrance is decoration, so it goes entirely under reduced motion
  // while every other behaviour stays.
  'motion-reduce:transition-none',
];

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Keep sequential focus inside the open dialog. A modal `<dialog>` makes the
 * rest of the document inert, but Tab past its last control still leaves for
 * the browser chrome before coming back; the MUI dialog this primitive replaces
 * wrapped immediately, and the shared logout confirmation is asserted that way.
 */
const wrapSequentialFocus = (event: React.KeyboardEvent<HTMLDialogElement>) => {
  if (event.key !== 'Tab' || event.defaultPrevented) {
    return;
  }

  const dialog = event.currentTarget;
  const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (candidate) => !candidate.closest('[inert]') && candidate.getClientRects().length > 0
  );

  if (focusable.length === 0) {
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = dialog.ownerDocument.activeElement;

  if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  } else if (event.shiftKey && (active === first || active === dialog)) {
    event.preventDefault();
    last.focus();
  }
};

/**
 * The native `<dialog>` element, shown with `showModal()`.
 *
 * The browser top layer paints above every z-index in the application — MUI's
 * 1300 modal plane and the Professional mobile drawer included — with no
 * arbitration code, and it makes the rest of the document inert, so there is
 * never a second focus trap to compete with. The element stays where it is
 * rendered in the React tree rather than being portalled, so it inherits the
 * `data-compass-surface` scope of its consumer and Layer 3 resolves normally.
 *
 * Two behaviours are deliberately taken away from the browser: `cancel` is
 * prevented so Escape becomes an `onClose` request the consumer answers, and
 * the previously focused element is captured and restored explicitly, which the
 * browser does not do when an open dialog is removed from the document.
 */
export const Dialog = forwardRef<HTMLDialogElement, DialogProps>(function Dialog(
  {open, onClose, initialFocusRef, className, children, ...props},
  ref
) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const openerRef = useRef<Element | null>(null);
  // Set while this component is the one closing the element, so the `close`
  // event that follows is recognised as its own and not answered twice.
  const closingRef = useRef(false);

  const restoreFocus = useCallback(() => {
    const opener = openerRef.current;

    openerRef.current = null;
    // The opener can have left the document — a control inside a dialog that
    // unmounts itself is the ordinary case — in which case there is nothing to
    // return to and focus stays where the browser put it.
    if (opener instanceof HTMLElement && opener.isConnected) {
      opener.focus();
    }
  }, []);

  useEffect(() => {
    const element = dialogRef.current;

    if (!element) {
      return;
    }

    if (open) {
      if (!element.open) {
        openerRef.current = element.ownerDocument.activeElement;
        element.showModal();
        initialFocusRef?.current?.focus();
      }
    } else if (element.open) {
      closingRef.current = true;
      element.close();
      restoreFocus();
    }
  }, [open, initialFocusRef, restoreFocus]);

  // An open dialog removed from the document takes the browser's own focus
  // restoration with it, so the primitive answers for that case explicitly.
  // Closing first clears the captured opener, so this is a no-op afterwards.
  useEffect(() => restoreFocus, [restoreFocus]);

  return (
    <dialog
      {...props}
      data-slot="dialog"
      onKeyDown={(event) => {
        props.onKeyDown?.(event);
        wrapSequentialFocus(event);
      }}
      onCancel={(event) => {
        // Without user activation the browser may issue a non-cancelable
        // request, then close the element regardless. Let onClose below
        // report that dismissal once instead of notifying the consumer twice.
        if (event.cancelable) {
          event.preventDefault();
          onClose();
        }
      }}
      onClose={() => {
        // A close this component did not drive: a form with `method="dialog"`,
        // a consumer calling `close()` on the forwarded ref, or a close request
        // the browser's close watcher would not let `cancel` prevent. Catching
        // the consumer up is what stops its `open` going stale — a stale `true`
        // leaves an element that is closed, invisible, and can never be shown
        // again, because nothing changes to re-run the effect above.
        if (closingRef.current) {
          closingRef.current = false;

          return;
        }

        restoreFocus();
        onClose();
      }}
      className={cn(dialogStyles, className)}
      ref={(node) => {
        dialogRef.current = node;

        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      }}>
      {children}
    </dialog>
  );
});

/*
 * Three neutral structural slots, and no more: between them they are the
 * scrolling contract. The header and footer do not shrink, the body takes the
 * remaining height and scrolls on its own, and that is what keeps a heading,
 * its controls and its actions reachable at 576px, below it, and at 400% zoom.
 *
 * Like the Card slots, none renders a heading element or a landmark role — a
 * consumer that needs an `<h2>` for `aria-labelledby` supplies its own.
 */

export const DialogHeader = forwardRef<HTMLDivElement, DialogSectionProps>(function DialogHeader(
  {className, ...props},
  ref
) {
  return (
    <div
      {...props}
      data-slot="dialog-header"
      className={cn(
        'flex shrink-0 items-center gap-3 border-b border-(--compass-border) p-4',
        className
      )}
      ref={ref}
    />
  );
});

export const DialogBody = forwardRef<HTMLDivElement, DialogSectionProps>(function DialogBody(
  {className, ...props},
  ref
) {
  return (
    <div
      {...props}
      data-slot="dialog-body"
      className={cn('min-h-0 flex-1 overflow-y-auto p-4', className)}
      ref={ref}
    />
  );
});

export const DialogFooter = forwardRef<HTMLDivElement, DialogSectionProps>(function DialogFooter(
  {className, ...props},
  ref
) {
  return (
    <div
      {...props}
      data-slot="dialog-footer"
      className={cn(
        'flex shrink-0 items-center justify-end gap-2 border-t border-(--compass-border) p-4',
        className
      )}
      ref={ref}
    />
  );
});
